import { z } from 'zod';
import { WorkOrderStatus, Priority } from '../types';

// ==========================================
// 1. SCHEMAS DE CLIENTES
// ==========================================
export const ClientSchema = z.object({
    name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres').max(100, 'El nombre es demasiado largo'),
    email: z.string().email('El correo electrónico no es válido').optional().or(z.literal('')),
    phone: z.string().min(6, 'El teléfono no es válido').max(20, 'El teléfono es demasiado largo').optional().or(z.literal('')),
    cif_nif: z.string().max(20).optional().or(z.literal('')),
    address: z.string().max(250).optional().or(z.literal('')),
    city: z.string().max(100).optional().or(z.literal('')),
    postal_code: z.string().max(20).optional().or(z.literal('')),
    country: z.string().max(100).optional().or(z.literal(''))
});

export const WorkSiteSchema = z.object({
    name: z.string().min(2, 'El nombre del centro debe tener al menos 2 caracteres'),
    location: z.string().optional().or(z.literal('')),
    contactPerson: z.string().optional().or(z.literal('')),
    phone: z.string().optional().or(z.literal(''))
});

// ==========================================
// 2. SCHEMAS DE PARTES DE TRABAJO
// ==========================================
export const NewOrderSchema = z.object({
    title: z.string().min(2, 'El título del parte debe tener al menos 2 caracteres'),
    clientId: z.string().min(1, 'Debes seleccionar un cliente válido'),
    obra: z.string().min(2, 'Debe especificar el centro/obra'),
    location: z.string().optional(),
    description: z.string().optional(),
    priority: z.nativeEnum(Priority).default(Priority.NORMAL),
    technicianId: z.string().nullable().optional(),
    scheduledDate: z.string().optional()
});

// A Zod schema for editing an existing order, allowing statuses and actual hours
export const EditOrderSchema = z.object({
    title: z.string().min(3, 'El título del parte debe tener al menos 3 caracteres'),
    description: z.string().optional(),
    priority: z.nativeEnum(Priority),
    status: z.nativeEnum(WorkOrderStatus),
    technicianId: z.string().uuid('Técnico inválido').nullable().optional(),
    actualHours: z.number().min(0, 'Las horas no pueden ser negativas').optional(),
    scheduledDate: z.string().optional()
});

// ==========================================
// 3. SCHEMAS DE TÉCNICOS (REPORTES Y FIRMAS)
// ==========================================
// Zod schema for closing/submitting work
export const TechWorkEntrySchema = z.object({
    hours: z.number().min(0.5, 'El tiempo trabajado debe ser de al menos 0.5 horas').max(24, 'Las horas superan el límite razonable diario'),
    notes: z.string().max(1000, 'Las consideraciones son demasiado largas').optional().or(z.literal('')),
});

export const TechMaterialSchema = z.object({
    materialDescription: z.string().min(2, 'El material debe tener un nombre válido').max(100, 'Descripción de material demasiado larga')
});

// ==========================================
// 4. SCHEMAS DE CONFIGURACIÓN & CUENTA
// ==========================================
export const ProfileUpdateSchema = z.object({
    full_name: z.string().min(3, 'El nombre debe tener al menos 3 caracteres'),
    preferred_language: z.enum(['es', 'en', 'ca']).optional()
});

export const CompanySettingsSchema = z.object({
    companyName: z.string().min(2, 'El nombre de la empresa es muy corto').max(100, 'Nombre de empresa muy largo'),
    supportEmail: z.string().email('Email de soporte no válido').optional().or(z.literal('')),
    phone: z.string().optional().or(z.literal('')),
});

// Helper function to process Zod errors into a single readable string
export const formatZodError = (error: any): string => {
    return error.errors ? error.errors.map((e: any) => e.message).join(', ') : 'Error de validación';
};
